import React, { useState, useEffect } from 'react';
import AuthService from '../../services/authService';
// Same source CreateAgentForm and the demo pickers read, so a preset's market
// can't drift into a different string for the same place.
import useTerritories from '../../hooks/useTerritories';

const PoolManagement = () => {
  const [pools, setPools] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    city: '',
    state: ''
  });
  const [selectedAgents, setSelectedAgents] = useState([]);
  // Which pool row is expanded to show its agent list inline
  const [expandedPoolId, setExpandedPoolId] = useState(null);
  const togglePoolExpanded = (poolId) => {
    setExpandedPoolId(prev => prev === poolId ? null : poolId);
  };

  // Pool search (filters the unified pools table across all states)
  const [poolSearchTerm, setPoolSearchTerm] = useState('');

  // ---- Pool Presets state ----
  const [presets, setPresets] = useState([]);
  const [expandedPresetId, setExpandedPresetId] = useState(null);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null); // null = create, object = edit
  const [presetForm, setPresetForm] = useState({ name: '', description: '', territory: '', pools: [] });

  const {
    names: territoryNames,
    loading: territoriesLoading,
    error: territoriesError,
  } = useTerritories();
  const [presetPoolSearch, setPresetPoolSearch] = useState('');
  // Search term for the Add Agents modal
  const [addAgentSearch, setAddAgentSearch] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const togglePresetExpanded = (id) => {
    setExpandedPresetId(prev => prev === id ? null : id);
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back-end-x7tx.onrender.com';

  useEffect(() => {
    fetchPools();
    fetchAgents();
    fetchPresets();
  }, []);

  const fetchPools = async () => {
    try {
      const token = AuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/agent-pools`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setPools(data.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pools:', error);
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const token = AuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/agents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      const agentsList = data.data || [];
      setAgents(agentsList);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const handleCreatePool = async (e) => {
    e.preventDefault();
    try {
      const token = AuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/agent-pools`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          serviceArea: {
            city: formData.city || null,
            state: formData.state || null
          }
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Pool created successfully!');
        setShowCreateModal(false);
        setFormData({ name: '', description: '', city: '', state: '' });
        fetchPools();
      } else {
        alert(data.message || 'Error creating pool');
      }
    } catch (error) {
      console.error('Error creating pool:', error);
      alert('Error creating pool');
    }
  };

  const handleAddAgentsToPool = async () => {
    if (selectedAgents.length === 0) {
      alert('Please select at least one agent');
      return;
    }

    try {
      const token = AuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/agent-pools/${selectedPool._id}/agents/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agentIds: selectedAgents
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(data.message);
        setShowAddAgentModal(false);
        setSelectedAgents([]);
        setSelectedPool(null);
        fetchPools();
      } else {
        alert(data.message || 'Error adding agents to pool');
      }
    } catch (error) {
      console.error('Error adding agents:', error);
      alert('Error adding agents to pool');
    }
  };

  const handleRemoveAgent = async (poolId, agentId) => {
    if (!window.confirm('Remove this agent from the pool?')) return;

    try {
      const token = AuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/agent-pools/${poolId}/agents/${agentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        alert('Agent removed successfully');
        fetchPools();
      } else {
        alert(data.message || 'Error removing agent');
      }
    } catch (error) {
      console.error('Error removing agent:', error);
      alert('Error removing agent');
    }
  };

  // ============ Pool Presets ============
  const fetchPresets = async () => {
    try {
      const response = await fetch(
        'https://back-end-x7tx.onrender.com/api/agent-pool-presets',
        {
          headers: { Authorization: `Bearer ${AuthService.getToken()}` },
        }
      );
      if (!response.ok) return;
      const data = await response.json();
      setPresets(data.data || []);
    } catch (err) {
      console.error('Error fetching presets:', err);
    }
  };

  const openCreatePresetModal = () => {
    setEditingPreset(null);
    setPresetForm({ name: '', description: '', territory: '', pools: [] });
    setPresetPoolSearch('');
    setShowPresetModal(true);
  };

  const openEditPresetModal = (preset) => {
    setEditingPreset(preset);
    setPresetForm({
      name: preset.name,
      description: preset.description || '',
      territory: preset.territory || '',
      pools: (preset.pools || []).map(p => (typeof p === 'object' ? p._id : p)),
    });
    setPresetPoolSearch('');
    setShowPresetModal(true);
  };

  const closePresetModal = () => {
    setShowPresetModal(false);
    setEditingPreset(null);
    setPresetForm({ name: '', description: '', territory: '', pools: [] });
    setPresetPoolSearch('');
    setSavingPreset(false);
  };

  const togglePresetPoolSelection = (poolId) => {
    setPresetForm(prev => {
      const exists = prev.pools.includes(poolId);
      return {
        ...prev,
        pools: exists ? prev.pools.filter(id => id !== poolId) : [...prev.pools, poolId],
      };
    });
  };

  const handleSavePreset = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!presetForm.name.trim()) return;
    setSavingPreset(true);
    try {
      const isEdit = !!editingPreset;
      const url = isEdit
        ? `https://back-end-x7tx.onrender.com/api/agent-pool-presets/${editingPreset._id}`
        : 'https://back-end-x7tx.onrender.com/api/agent-pool-presets';
      const method = isEdit ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${AuthService.getToken()}`,
        },
        body: JSON.stringify({
          name: presetForm.name.trim(),
          description: presetForm.description.trim(),
          // Drives which agents see this county in onboarding. '' = unassigned,
          // which means it simply never surfaces there.
          territory: presetForm.territory,
          pools: presetForm.pools,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        alert(data.error || 'Failed to save preset');
        setSavingPreset(false);
        return;
      }
      await fetchPresets();
      closePresetModal();
    } catch (err) {
      console.error('Error saving preset:', err);
      alert('Failed to save preset');
      setSavingPreset(false);
    }
  };

  const handleDeletePreset = async (preset) => {
    if (!window.confirm(`Delete preset "${preset.name}"? This won't delete any pools.`)) return;
    try {
      const response = await fetch(
        `https://back-end-x7tx.onrender.com/api/agent-pool-presets/${preset._id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${AuthService.getToken()}` },
        }
      );
      if (!response.ok) {
        alert('Failed to delete preset');
        return;
      }
      await fetchPresets();
    } catch (err) {
      console.error('Error deleting preset:', err);
    }
  };

  const handleDeletePool = async (poolId) => {
    if (!window.confirm('Are you sure you want to delete this pool? This cannot be undone.')) return;

    try {
      const token = AuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/agent-pools/${poolId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        alert('Pool deleted successfully');
        fetchPools();
      } else {
        alert(data.message || 'Error deleting pool');
      }
    } catch (error) {
      console.error('Error deleting pool:', error);
      alert('Error deleting pool');
    }
  };

  const toggleAgentSelection = (agentId) => {
    setSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading pools...</div>
      </div>
    );
  }

  // Group pools by state for the horizontal table view
  const poolsByState = pools.reduce((acc, pool) => {
    const state = pool.serviceArea?.state?.trim() || 'Unassigned';
    if (!acc[state]) acc[state] = [];
    acc[state].push(pool);
    return acc;
  }, {});
  // Sort: known states alphabetically, "Unassigned" always last
  const stateGroups = Object.keys(poolsByState).sort((a, b) => {
    if (a === 'Unassigned') return 1;
    if (b === 'Unassigned') return -1;
    return a.localeCompare(b);
  });

  return (
    <div className="admin-panel" style={styles.container}>
      {/* Wordmark header */}
      <div style={styles.wordmark}>
        <img src="/lauft-logo.png" alt="Lauft" style={styles.wordmarkLogo} />
        <span style={styles.wordmarkText}>Agent Pools</span>
      </div>

      {/* Top meta bar */}
      <div style={styles.topBar}>
        <div style={styles.topBarMeta}>
          {pools.length} {pools.length === 1 ? 'pool' : 'pools'} across {stateGroups.length} {stateGroups.length === 1 ? 'state' : 'states'}
        </div>
      </div>

      {/* Empty state */}
      {pools.length === 0 && (
        <div style={styles.emptyState}>
          <p style={styles.emptyStateText}>No pools created yet</p>
          <p style={styles.emptyStateSubtext}>Create your first pool to get started</p>
        </div>
      )}

      {/* Pool Presets section */}
      <div style={styles.presetSection}>
        <div style={styles.stateGroupHeader}>
          <span style={styles.stateGroupLabel}>Pool Presets</span>
          <span style={styles.stateGroupCount}>
            {presets.length} {presets.length === 1 ? 'preset' : 'presets'}
          </span>
        </div>

        <div style={styles.tableCard}>
          <div style={styles.tableHeaderBar}>
            <span style={styles.tableHeaderHint}>
              {presets.length > 0 ? 'Click a preset to expand or edit' : 'Named groupings of pools for quick one-click assignment'}
            </span>
            <button
              type="button"
              onClick={openCreatePresetModal}
              style={styles.headerAddBtn}
              title="Create preset"
            >
              +
            </button>
          </div>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, minWidth: '220px'}}>Preset</th>
                  <th style={styles.th}>Market</th>
                  <th style={styles.th}>Pools</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
                <tbody>
                  {presets.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{...styles.td, padding: '28px 14px', color: '#9ca3af', fontSize: '12px'}}>
                        No presets yet — click the <strong style={{color: '#2563eb'}}>+</strong> above to create one.
                      </td>
                    </tr>
                  )}
                  {presets.map(preset => {
                    const poolCount = preset.pools?.length || 0;
                    const isExpanded = expandedPresetId === preset._id;
                    return (
                      <React.Fragment key={preset._id}>
                        <tr style={styles.tr}>
                          <td style={styles.td}>
                            <div style={styles.poolNameCell}>
                              <span style={styles.poolNameText}>{preset.name}</span>
                              {preset.description && (
                                <span style={styles.poolDescText}>{preset.description}</span>
                              )}
                            </div>
                          </td>
                          <td style={styles.td}>
                            {preset.territory ? (
                              <span style={styles.marketBadge}>{preset.territory}</span>
                            ) : (
                              // Actionable, not decorative: an unassigned preset is
                              // invisible to the agent onboarding pool step.
                              <span style={styles.marketBadgeEmpty} title="Not shown in agent onboarding until a market is set">
                                Unassigned
                              </span>
                            )}
                          </td>
                          <td style={styles.td}>
                            {poolCount > 0 ? (
                              <button
                                type="button"
                                onClick={() => togglePresetExpanded(preset._id)}
                                style={styles.agentCountBtn}
                                title={isExpanded ? 'Hide pools' : 'View pools'}
                              >
                                {poolCount} {poolCount === 1 ? 'pool' : 'pools'}
                                <span style={{...styles.chevron, transform: isExpanded ? 'rotate(180deg)' : 'none'}}>
                                  ▾
                                </span>
                              </button>
                            ) : (
                              <span style={styles.zeroAgents}>0 pools</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            <div style={styles.actionsRow}>
                              <button
                                onClick={() => openEditPresetModal(preset)}
                                style={styles.actionBtn}
                                title="Edit preset"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeletePreset(preset)}
                                style={{...styles.actionBtn, ...styles.actionBtnDanger}}
                                title="Delete preset"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && poolCount > 0 && (
                          <tr style={styles.expandedRow}>
                            <td colSpan={4} style={styles.expandedCell}>
                              <div style={styles.agentChipList}>
                                {preset.pools.map(p => (
                                  <div key={p._id || p} style={styles.presetPoolChip}>
                                    <span style={styles.agentChipName}>{p.name || 'Unknown Pool'}</span>
                                    {p.serviceArea?.city && (
                                      <span style={styles.agentChipMeta}>
                                        {p.serviceArea.city}{p.serviceArea.state ? `, ${p.serviceArea.state}` : ''}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
      </div>

      {/* Unified pools table with state dividers */}
      {(() => {
        // Filter all pools by the search term (name, description, city, state)
        const q = poolSearchTerm.trim().toLowerCase();
        const matches = (pool) => {
          if (!q) return true;
          const name = (pool.name || '').toLowerCase();
          const desc = (pool.description || '').toLowerCase();
          const city = (pool.serviceArea?.city || '').toLowerCase();
          const st = (pool.serviceArea?.state || '').toLowerCase();
          return name.includes(q) || desc.includes(q) || city.includes(q) || st.includes(q);
        };

        // Build a flat list of visible rows, interleaving state-divider rows between groups
        const rows = [];
        stateGroups.forEach(state => {
          const visible = poolsByState[state].filter(matches);
          if (visible.length === 0) return;
          rows.push({ type: 'divider', state, count: visible.length });
          visible.forEach(pool => rows.push({ type: 'pool', pool }));
        });

        const totalVisible = rows.filter(r => r.type === 'pool').length;

        return (
          <div style={styles.stateGroup}>
            <div style={styles.stateGroupHeader}>
              <span style={styles.stateGroupLabel}>Pools</span>
              <span style={styles.stateGroupCount}>
                {totalVisible} {totalVisible === 1 ? 'pool' : 'pools'}
                {q && pools.length !== totalVisible ? ` of ${pools.length}` : ''}
              </span>
            </div>

            <div style={styles.tableCard}>
              {/* Search bar in card header */}
              <div style={styles.tableHeaderBar}>
                <div style={styles.searchBox}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search pools by name, city, or state..."
                    value={poolSearchTerm}
                    onChange={(e) => setPoolSearchTerm(e.target.value)}
                    style={styles.searchInput}
                  />
                  {poolSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setPoolSearchTerm('')}
                      style={styles.searchClearBtn}
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  style={styles.headerAddBtn}
                  title="Create pool"
                >
                  +
                </button>
              </div>

              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{...styles.th, minWidth: '200px'}}>Pool</th>
                      <th style={styles.th}>Location</th>
                      <th style={styles.th}>Agents</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={4} style={{...styles.td, textAlign: 'left', padding: '28px 14px', color: '#9ca3af', fontSize: '12px'}}>
                          {q ? `No pools match "${poolSearchTerm}"` : 'No pools yet.'}
                        </td>
                      </tr>
                    )}
                    {rows.map((row, idx) => {
                      if (row.type === 'divider') {
                        return (
                          <tr key={`divider-${row.state}`} style={styles.dividerRow}>
                            <td colSpan={4} style={styles.dividerCell}>
                              <span style={styles.dividerLabel}>{row.state}</span>
                              <span style={styles.dividerCount}>
                                {row.count} {row.count === 1 ? 'pool' : 'pools'}
                              </span>
                            </td>
                          </tr>
                        );
                      }
                      const pool = row.pool;
                      const agentCount = pool.agents?.length || 0;
                      const isExpanded = expandedPoolId === pool._id;
                      return (
                        <React.Fragment key={pool._id}>
                          <tr style={styles.tr}>
                            <td style={styles.td}>
                              <div style={styles.poolNameCell}>
                                <span style={styles.poolNameText}>{pool.name}</span>
                                {pool.description && (
                                  <span style={styles.poolDescText}>{pool.description}</span>
                                )}
                              </div>
                            </td>
                            <td style={styles.td}>
                              {pool.serviceArea?.city ? (
                                <span style={styles.locationPill}>
                                  {pool.serviceArea.city}{pool.serviceArea.state && `, ${pool.serviceArea.state}`}
                                </span>
                              ) : pool.serviceArea?.state ? (
                                <span style={styles.locationPill}>{pool.serviceArea.state}</span>
                              ) : (
                                <span style={styles.dimPill}>Default · All Locations</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {agentCount > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => togglePoolExpanded(pool._id)}
                                  style={styles.agentCountBtn}
                                  title={isExpanded ? 'Hide agents' : 'View agents'}
                                >
                                  {agentCount} {agentCount === 1 ? 'agent' : 'agents'}
                                  <span style={{...styles.chevron, transform: isExpanded ? 'rotate(180deg)' : 'none'}}>
                                    ▾
                                  </span>
                                </button>
                              ) : (
                                <span style={styles.zeroAgents}>0 agents</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              <div style={styles.actionsRow}>
                                <button
                                  onClick={() => {
                                    setSelectedPool(pool);
                                    setAddAgentSearch('');
                                    setSelectedAgents([]);
                                    setShowAddAgentModal(true);
                                  }}
                                  style={styles.actionBtn}
                                  title="Add Agents"
                                >
                                  + Add
                                </button>
                                <button
                                  onClick={() => handleDeletePool(pool._id)}
                                  style={{...styles.actionBtn, ...styles.actionBtnDanger}}
                                  title="Delete Pool"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && agentCount > 0 && (
                            <tr style={styles.expandedRow}>
                              <td colSpan={4} style={styles.expandedCell}>
                                <div style={styles.agentChipList}>
                                  {pool.agents.map(agent => (
                                    <div key={agent._id} style={styles.agentChip}>
                                      <div style={styles.agentChipInfo}>
                                        <span style={styles.agentChipName}>{agent.name}</span>
                                        <span style={styles.agentChipMeta}>
                                          {agent.email}{agent.pointsBalance !== undefined && ` · ${agent.pointsBalance} pts`}
                                        </span>
                                      </div>
                                      <button
                                        onClick={() => handleRemoveAgent(pool._id, agent._id)}
                                        style={styles.chipRemoveBtn}
                                        title="Remove from pool"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Create Pool Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Create New Pool</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                style={styles.closeButton}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreatePool} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Pool Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={styles.input}
                  required
                  placeholder="e.g., Denver, Los Angeles, Default"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., Denver metro area agents"
                />
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    style={styles.input}
                    placeholder="e.g., Denver"
                  />
                  <small style={styles.hint}>Leave blank for state-wide or default pool</small>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    style={styles.input}
                    placeholder="e.g., Colorado"
                  />
                </div>
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Create Pool
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Agents Modal */}
      {showAddAgentModal && selectedPool && (
        <div style={styles.modalOverlay} onClick={() => setShowAddAgentModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Add Agents to {selectedPool.name}</h2>
              <button
                onClick={() => setShowAddAgentModal(false)}
                style={styles.closeButton}
              >
                ✕
              </button>
            </div>

            {/* Search bar */}
            <div style={styles.addAgentSearchBar}>
              <div style={styles.searchBox}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, email, or agency..."
                  value={addAgentSearch}
                  onChange={(e) => setAddAgentSearch(e.target.value)}
                  style={styles.searchInput}
                  autoFocus
                />
                {addAgentSearch && (
                  <button
                    type="button"
                    onClick={() => setAddAgentSearch('')}
                    style={styles.searchClearBtn}
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div style={styles.agentSelectionList}>
              {(() => {
                if (!agents || agents.length === 0) {
                  return <p style={styles.emptyText}>No agents available</p>;
                }
                const q = addAgentSearch.trim().toLowerCase();
                const filtered = q
                  ? agents.filter(a => {
                      const name = (a.name || '').toLowerCase();
                      const email = (a.email || '').toLowerCase();
                      const agency = (a.agentInfo?.agency || '').toLowerCase();
                      return name.includes(q) || email.includes(q) || agency.includes(q);
                    })
                  : agents;
                if (filtered.length === 0) {
                  return <p style={styles.emptyText}>No agents match "{addAgentSearch}"</p>;
                }
                return filtered.map(agent => {
                  const isInPool = selectedPool.agents?.some(a => a._id === agent._id);
                  const isSelected = selectedAgents.includes(agent._id);
                  return (
                    <div
                      key={agent._id}
                      style={{
                        ...styles.selectableAgent,
                        ...(isInPool ? styles.agentInPool : {}),
                        ...(isSelected ? styles.agentSelected : {})
                      }}
                      onClick={() => !isInPool && toggleAgentSelection(agent._id)}
                    >
                      <div style={styles.agentSelectInfo}>
                        <span style={styles.agentSelectName}>{agent.name}</span>
                        <span style={styles.agentSelectEmail}>
                          {agent.email}{agent.agentInfo?.agency ? ` · ${agent.agentInfo.agency}` : ''}
                        </span>
                      </div>
                      {isInPool && <span style={styles.inPoolBadge}>Already in pool</span>}
                      {!isInPool && isSelected && <span style={styles.selectedBadge}>✓</span>}
                    </div>
                  );
                });
              })()}
            </div>

            <div style={styles.modalActions}>
              <button
                onClick={() => setShowAddAgentModal(false)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleAddAgentsToPool}
                style={styles.submitButton}
                disabled={selectedAgents.length === 0}
              >
                Add {selectedAgents.length} Agent{selectedAgents.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Preset Modal */}
      {showPresetModal && (() => {
        const q = presetPoolSearch.trim().toLowerCase();
        const filteredPools = q
          ? pools.filter(p => {
              const city = (p.serviceArea?.city || '').toLowerCase();
              const state = (p.serviceArea?.state || '').toLowerCase();
              const name = (p.name || '').toLowerCase();
              return name.includes(q) || city.includes(q) || state.includes(q);
            })
          : pools;
        const selectedCount = presetForm.pools.length;
        return (
          <div style={styles.modalOverlay} onClick={closePresetModal}>
            <div
              style={{...styles.modal, width: '640px'}}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>
                  {editingPreset ? 'Edit Preset' : 'Create New Preset'}
                </h2>
                <button onClick={closePresetModal} style={styles.closeButton}>
                  ✕
                </button>
              </div>

              <form onSubmit={handleSavePreset} style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Preset Name *</label>
                  <input
                    type="text"
                    value={presetForm.name}
                    onChange={(e) => setPresetForm({...presetForm, name: e.target.value})}
                    style={styles.input}
                    required
                    placeholder="e.g., Houston Metro, Colorado Front Range"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Market</label>
                  <select
                    value={presetForm.territory}
                    onChange={(e) => setPresetForm({...presetForm, territory: e.target.value})}
                    style={styles.input}
                    disabled={territoriesLoading || !!territoriesError}
                  >
                    <option value="">
                      {territoriesLoading
                        ? 'Loading markets…'
                        : territoriesError
                          ? 'Markets unavailable'
                          : 'Unassigned'}
                    </option>
                    {/* A market that's since been retired or renamed would
                        otherwise silently reset to Unassigned on save. */}
                    {presetForm.territory && !territoryNames.includes(presetForm.territory) && (
                      <option value={presetForm.territory}>
                        {presetForm.territory} (inactive)
                      </option>
                    )}
                    {territoryNames.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <span style={styles.fieldHint}>
                    Agents in this market see this county in their onboarding pool
                    step. Leave unassigned to keep it admin-only.
                  </span>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description</label>
                  <input
                    type="text"
                    value={presetForm.description}
                    onChange={(e) => setPresetForm({...presetForm, description: e.target.value})}
                    style={styles.input}
                    placeholder="Optional notes about when to use this preset"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Pools · {selectedCount} selected
                  </label>
                  <input
                    type="text"
                    value={presetPoolSearch}
                    onChange={(e) => setPresetPoolSearch(e.target.value)}
                    style={{...styles.input, marginBottom: '8px'}}
                    placeholder="Filter by name, city, or state..."
                  />
                  <div style={styles.presetPoolPicker}>
                    {filteredPools.length === 0 ? (
                      <p style={styles.emptyText}>
                        {pools.length === 0 ? 'No pools available' : 'No pools match your search'}
                      </p>
                    ) : (
                      filteredPools.map(pool => {
                        const isSel = presetForm.pools.includes(pool._id);
                        return (
                          <div
                            key={pool._id}
                            style={{
                              ...styles.selectableAgent,
                              ...(isSel ? styles.agentSelected : {}),
                            }}
                            onClick={() => togglePresetPoolSelection(pool._id)}
                          >
                            <div style={styles.agentSelectInfo}>
                              <span style={styles.agentSelectName}>{pool.name}</span>
                              <span style={styles.agentSelectEmail}>
                                {pool.serviceArea?.city
                                  ? `${pool.serviceArea.city}${pool.serviceArea.state ? `, ${pool.serviceArea.state}` : ''}`
                                  : 'Default · All locations'}
                              </span>
                            </div>
                            {isSel && <span style={styles.selectedBadge}>✓</span>}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                <div style={styles.modalActions}>
                  <button
                    type="button"
                    onClick={closePresetModal}
                    style={styles.cancelButton}
                    disabled={savingPreset}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      ...styles.submitButton,
                      ...(savingPreset || !presetForm.name.trim() ? styles.buttonDisabled : {}),
                    }}
                    disabled={savingPreset || !presetForm.name.trim()}
                  >
                    {savingPreset
                      ? (editingPreset ? 'Saving…' : 'Creating…')
                      : (editingPreset ? 'Save Changes' : 'Create Preset')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    padding: '32px 0 0',
    color: '#111',
  },

  // Wordmark header
  wordmark: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  wordmarkLogo: {
    width: '22px',
    height: '22px',
    objectFit: 'contain',
  },
  wordmarkText: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    color: '#888',
    textTransform: 'uppercase',
  },

  // Top bar
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    gap: '12px',
  },
  topBarMeta: {
    fontSize: '12px',
    color: '#9ca3af',
    fontWeight: 500,
  },
  createButton: {
    padding: '8px 16px',
    borderRadius: '9px',
    border: 'none',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.35)',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },

  // State group
  stateGroup: {
    marginBottom: '28px',
  },
  stateGroupHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    padding: '0 4px 10px',
  },
  stateGroupLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#9ca3af',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  stateGroupCount: {
    fontSize: '10px',
    fontWeight: 600,
    color: '#d1d5db',
    letterSpacing: '0.05em',
  },

  // Table card
  tableCard: {
    background: '#ffffff',
    border: '1px solid #f0f0f0',
    borderRadius: '14px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
    overflow: 'hidden',
  },
  tableContainer: {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '520px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: '#fff',
    textAlign: 'left',
  },
  marketBadge: {
    display: 'inline-block',
    padding: '3px 9px',
    borderRadius: '999px',
    background: 'rgba(37, 99, 235, 0.08)',
    border: '1px solid rgba(37, 99, 235, 0.18)',
    color: '#1e40af',
    fontSize: '11px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  marketBadgeEmpty: {
    display: 'inline-block',
    padding: '3px 9px',
    borderRadius: '999px',
    background: '#fafafa',
    border: '1px dashed #e5e7eb',
    color: '#9ca3af',
    fontSize: '11px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    cursor: 'help',
  },
  fieldHint: {
    display: 'block',
    marginTop: '6px',
    fontSize: '11px',
    lineHeight: 1.5,
    color: '#9ca3af',
  },
  th: {
    position: 'sticky',
    top: 0,
    background: '#fafafa',
    padding: '11px 14px',
    textAlign: 'left',
    fontWeight: 700,
    fontSize: '10px',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid #f0f0f0',
  },
  tr: {
    transition: 'background 0.1s',
  },
  td: {
    padding: '14px',
    borderBottom: '1px solid #f5f5f5',
    color: '#111',
    fontSize: '13px',
    verticalAlign: 'middle',
    textAlign: 'left',
  },

  // Pool name cell
  poolNameCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  poolNameText: {
    color: '#111',
    fontSize: '13px',
    fontWeight: 600,
  },
  poolDescText: {
    color: '#9ca3af',
    fontSize: '11px',
  },

  // Location pills
  locationPill: {
    display: 'inline-block',
    padding: '2px 7px',
    borderRadius: '999px',
    fontSize: '10px',
    fontWeight: 600,
    color: '#6b7280',
    background: '#f3f4f6',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  dimPill: {
    display: 'inline-block',
    padding: '2px 7px',
    borderRadius: '999px',
    fontSize: '10px',
    fontWeight: 500,
    color: '#d1d5db',
    background: 'transparent',
    border: '1px dashed #e5e7eb',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },

  // Agent count button
  agentCountBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    background: 'rgba(37, 99, 235, 0.08)',
    border: 'none',
    borderRadius: '999px',
    color: '#1d4ed8',
    fontSize: '11px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    letterSpacing: '0.02em',
    transition: 'background 0.15s',
  },
  zeroAgents: {
    display: 'inline-block',
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#d1d5db',
  },
  chevron: {
    fontSize: '10px',
    transition: 'transform 0.2s',
    lineHeight: 1,
  },

  // Actions
  actionsRow: {
    display: 'flex',
    gap: '6px',
    justifyContent: 'flex-start',
  },
  actionBtn: {
    padding: '5px 11px',
    borderRadius: '7px',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#6b7280',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  actionBtnDanger: {
    color: '#dc2626',
    borderColor: 'rgba(220, 38, 38, 0.2)',
  },

  // Expanded row with agents
  expandedRow: {
    background: '#fafafa',
  },
  expandedCell: {
    padding: '14px',
    borderBottom: '1px solid #f5f5f5',
    background: '#fafafa',
  },
  agentChipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  agentChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    padding: '7px 10px 7px 12px',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '9px',
  },
  agentChipInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  agentChipName: {
    color: '#111',
    fontSize: '12px',
    fontWeight: 600,
  },
  agentChipMeta: {
    color: '#9ca3af',
    fontSize: '10px',
  },
  chipRemoveBtn: {
    background: 'transparent',
    border: 'none',
    color: '#d1d5db',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '6px',
    fontSize: '13px',
    lineHeight: 1,
    fontFamily: 'inherit',
  },

  // Loading
  loadingText: {
    color: '#9ca3af',
    fontSize: '13px',
    textAlign: 'center',
    padding: '48px',
  },

  // Empty state
  emptyState: {
    background: '#ffffff',
    border: '1px solid #f0f0f0',
    borderRadius: '14px',
    padding: '48px 20px',
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
  },
  emptyStateText: {
    color: '#111',
    fontSize: '14px',
    fontWeight: 600,
    margin: '0 0 4px',
  },
  emptyStateSubtext: {
    color: '#9ca3af',
    fontSize: '12px',
    margin: 0,
  },

  // Modal overlay
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(17, 17, 17, 0.5)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    background: '#ffffff',
    borderRadius: '16px',
    width: '520px',
    maxWidth: '100%',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
    border: '1px solid #f0f0f0',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #f0f0f0',
    position: 'sticky',
    top: 0,
    background: '#ffffff',
    zIndex: 2,
  },
  modalTitle: {
    fontSize: '16px',
    fontWeight: 700,
    margin: 0,
    color: '#111',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: '8px',
    fontFamily: 'inherit',
  },

  // Form
  form: {
    padding: '20px 24px 24px',
  },
  formGroup: {
    marginBottom: '14px',
    flex: 1,
  },
  formRow: {
    display: 'flex',
    gap: '12px',
  },
  label: {
    display: 'block',
    color: '#9ca3af',
    fontSize: '11px',
    fontWeight: 600,
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '9px',
    fontSize: '13px',
    color: '#111',
    background: '#fff',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  hint: {
    display: 'block',
    marginTop: '4px',
    fontSize: '10px',
    color: '#d1d5db',
    fontStyle: 'normal',
  },

  // Modal actions
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    paddingTop: '12px',
    marginTop: '8px',
    borderTop: '1px solid #f0f0f0',
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '20px',
    marginLeft: '-24px',
    marginRight: '-24px',
  },
  cancelButton: {
    padding: '9px 16px',
    borderRadius: '9px',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#6b7280',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  submitButton: {
    padding: '9px 18px',
    borderRadius: '9px',
    border: 'none',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.35)',
    fontFamily: 'inherit',
  },

  // Add Agents modal — agent selection list
  agentSelectionList: {
    padding: '16px 24px',
    maxHeight: '50vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  selectableAgent: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '9px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    background: '#fff',
    textAlign: 'left',
  },
  agentInPool: {
    opacity: 0.5,
    cursor: 'not-allowed',
    background: '#fafafa',
  },
  agentSelected: {
    background: 'rgba(37, 99, 235, 0.06)',
    borderColor: '#2563eb',
  },
  agentSelectInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '1px',
    textAlign: 'left',
    flex: 1,
    minWidth: 0,
  },
  agentSelectName: {
    color: '#111',
    fontSize: '13px',
    fontWeight: 600,
    textAlign: 'left',
    width: '100%',
  },
  agentSelectEmail: {
    color: '#9ca3af',
    fontSize: '11px',
    textAlign: 'left',
    width: '100%',
  },
  inPoolBadge: {
    fontSize: '10px',
    color: '#9ca3af',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginLeft: 'auto',
    flexShrink: 0,
  },
  selectedBadge: {
    marginLeft: 'auto',
    flexShrink: 0,
    color: '#2563eb',
    fontSize: '14px',
    fontWeight: 700,
  },

  // Table header bar (search row above the thead)
  tableHeaderBar: {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  tableHeaderHint: {
    fontSize: '11px',
    color: '#9ca3af',
    fontWeight: 500,
    letterSpacing: '0.01em',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    maxWidth: '420px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    color: '#111',
    background: 'transparent',
    fontFamily: 'inherit',
    padding: 0,
  },
  searchClearBtn: {
    background: 'transparent',
    border: 'none',
    color: '#9ca3af',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
    fontFamily: 'inherit',
  },

  // State divider row inside the unified pool table
  dividerRow: {
    background: '#fafafa',
  },
  dividerCell: {
    padding: '10px 14px',
    textAlign: 'left',
    borderBottom: '1px solid #f0f0f0',
    borderTop: '1px solid #f0f0f0',
    background: '#fafafa',
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
  },
  dividerLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#9ca3af',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  dividerCount: {
    fontSize: '10px',
    fontWeight: 600,
    color: '#d1d5db',
    letterSpacing: '0.05em',
  },

  // Search bar in the Add Agents modal (above the agent selection list)
  addAgentSearchBar: {
    padding: '12px 24px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fff',
  },

  // Inline + button in the Pool Presets table header
  headerAddBtn: {
    width: '22px',
    height: '22px',
    borderRadius: '6px',
    border: '1.5px solid #2563eb',
    background: '#fff',
    color: '#2563eb',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },

  // Per-state pagination
  statePagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
    marginTop: '12px',
    padding: '0 4px',
  },
  pageBtn: {
    padding: '5px 10px',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '7px',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '11px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
    minWidth: '26px',
  },
  pageBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  pageInfo: {
    padding: '5px 10px',
    color: '#9ca3af',
    fontSize: '11px',
    fontWeight: 500,
  },

  // Legacy/unused — retained to avoid breaking any references if they sneak through
  emptyText: {
    color: '#9ca3af',
    fontSize: '12px',
    textAlign: 'center',
    padding: '20px',
  },

  // ---- Pool Presets ----
  presetSection: {
    marginBottom: '32px',
  },
  presetHeaderBar: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '14px',
    padding: '0 4px',
  },
  presetSubtext: {
    fontSize: '11px',
    color: '#9ca3af',
    marginTop: '3px',
    fontWeight: 500,
  },
  createPresetBtn: {
    padding: '7px 14px',
    borderRadius: '9px',
    border: '1px solid #2563eb',
    background: '#fff',
    color: '#2563eb',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s',
  },
  presetPoolChip: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '1px',
    padding: '7px 12px',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '9px',
  },
  presetPoolPicker: {
    maxHeight: '280px',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '9px',
    padding: '8px',
    background: '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
};

export default PoolManagement;